import React, { useState, useContext, useEffect, useMemo } from "react";
import {
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Alert,
} from "@mui/material";
import {
  Business,
  Person,
  Settings,
  CheckCircle,
  ArrowBack,
  ArrowForward,
} from "@mui/icons-material";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import api from "../../services/api";
import usePlans from "../../hooks/usePlans";
import useModules from "../../hooks/useModules";
import { useTranslation } from "react-i18next";

const RegisterClient = () => {
  const { user } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const { getPlanList } = usePlans();
  const { list: listModules } = useModules();

  const [plans, setPlans] = useState([]);
  const [addonsList, setAddonsList] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    // Company Info
    companyName: "",
    companyEmail: "",
    companyPhone: "",
    companyAddress: "",
    industry: "",
    website: "",

    // Contact Person
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    contactPosition: "",

    // Plan Selection
    planId: "",
    billingCycle: "monthly",
    addons: [],

    // Notes
    notes: "",
  });

  const steps = useMemo(
    () => [t("registerClient.steps.company"), t("registerClient.steps.contact"), t("registerClient.steps.plan")],
    [t]
  );

  const numberLocale = i18n.language || undefined;
  const formatMoney = (value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return "0.00";
    return new Intl.NumberFormat(numberLocale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  };

  useEffect(() => {
    async function fetchCatalog() {
      try {
        const planList = await getPlanList();
        setPlans(planList || []);
        if (!formData.planId && planList && planList.length > 0) {
          setFormData((prev) => ({ ...prev, planId: planList[0].id }));
        }
      } catch (error) {
        console.error("Error fetching plans:", error);
      }

      try {
        const modules = await listModules({ active_only: true });
        const addons = (modules || []).filter((m) => m.is_addon === true);
        setAddonsList(addons);
      } catch (error) {
        console.error("Error fetching addons:", error);
      }
    }
    fetchCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const industries = useMemo(
    () => [
      { value: "retail", label: t("registerClient.industries.retail") },
      { value: "restaurant", label: t("registerClient.industries.restaurant") },
      { value: "healthcare", label: t("registerClient.industries.healthcare") },
      { value: "education", label: t("registerClient.industries.education") },
      { value: "technology", label: t("registerClient.industries.technology") },
      { value: "services", label: t("registerClient.industries.services") },
      { value: "ecommerce", label: t("registerClient.industries.ecommerce") },
      { value: "real_estate", label: t("registerClient.industries.realEstate") },
      { value: "other", label: t("registerClient.industries.other") },
    ],
    [t]
  );

  const handleNext = () => {
    // Validation per step
    if (activeStep === 0) {
      if (!formData.companyName || !formData.companyEmail || !formData.companyPhone) {
        toast.error(t("registerClient.toasts.stepCompanyRequired"));
        return;
      }
    }
    if (activeStep === 1) {
      if (!formData.contactName || !formData.contactEmail) {
        toast.error(t("registerClient.toasts.stepContactRequired"));
        return;
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      // Create company and user
      const payload = {
        ...formData,
        vendedorId: user.id,
      };

      await api.post("/companies/register", {
        name: payload.companyName,
        email: payload.companyEmail,
        phone: payload.companyPhone,
        planId: payload.planId,
        address: payload.companyAddress,
        industry: payload.industry,
        website: payload.website,
        contactName: payload.contactName,
        contactEmail: payload.contactEmail,
        contactPhone: payload.contactPhone,
        contactPosition: payload.contactPosition,
        billingCycle: payload.billingCycle,
        addons: payload.addons,
        notes: payload.notes,
        vendedorId: payload.vendedorId,
      });

      toast.success(t("registerClient.toasts.success"));
      setTimeout(() => {
        window.location.href = "/leads";
      }, 2000);
    } catch (error) {
      console.error("Error registering client:", error);
      toast.error(t("registerClient.toasts.error"));
    }
  };

  const handleAddonToggle = (addonKey) => {
    setFormData((prev) => ({
      ...prev,
      addons: prev.addons.includes(addonKey)
        ? prev.addons.filter((id) => id !== addonKey)
        : [...prev.addons, addonKey],
    }));
  };

  const calculateTotal = () => {
    const plan = plans.find((p) => p.id === formData.planId) || {};
    const addonsCost = formData.addons.reduce((sum, addonKey) => {
      const addon = addonsList.find((a) => a.key === addonKey);
      return sum + (addon ? Number(addon.amount || 0) : 0);
    }, 0);
    const multiplier = formData.billingCycle === "annual" ? 10 : 1;
    return (Number(plan.amount || 0) + addonsCost) * multiplier;
  };

  return (
    <Paper
      sx={{
        p: 3,
        minHeight: "calc(100vh - 100px)",
      }}
      elevation={0}
    >
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          {t("registerClient.title")}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {t("registerClient.subtitle")}
        </Typography>
      </Box>

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step Content */}
      <Box sx={{ mt: 3 }}>
        {/* Step 0: Company Info */}
        {activeStep === 0 && (
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Business sx={{ mr: 1, color: "#6366f1", fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600}>
                  {t("registerClient.company.title")}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label={t("registerClient.company.fields.name")}
                    value={formData.companyName}
                    onChange={(e) =>
                      setFormData({ ...formData, companyName: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t("registerClient.company.fields.industry")}</InputLabel>
                    <Select
                      value={formData.industry}
                      onChange={(e) =>
                        setFormData({ ...formData, industry: e.target.value })
                      }
                      label={t("registerClient.company.fields.industry")}
                    >
                      {industries.map((ind) => (
                        <MenuItem key={ind.value} value={ind.value}>
                          {ind.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label={t("registerClient.company.fields.email")}
                    type="email"
                    value={formData.companyEmail}
                    onChange={(e) =>
                      setFormData({ ...formData, companyEmail: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label={t("registerClient.company.fields.phone")}
                    value={formData.companyPhone}
                    onChange={(e) =>
                      setFormData({ ...formData, companyPhone: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("registerClient.company.fields.address")}
                    value={formData.companyAddress}
                    onChange={(e) =>
                      setFormData({ ...formData, companyAddress: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("registerClient.company.fields.website")}
                    value={formData.website}
                    onChange={(e) =>
                      setFormData({ ...formData, website: e.target.value })
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Contact Person */}
        {activeStep === 1 && (
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Person sx={{ mr: 1, color: "#10b981", fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600}>
                  {t("registerClient.contact.title")}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label={t("registerClient.contact.fields.name")}
                    value={formData.contactName}
                    onChange={(e) =>
                      setFormData({ ...formData, contactName: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("registerClient.contact.fields.position")}
                    value={formData.contactPosition}
                    onChange={(e) =>
                      setFormData({ ...formData, contactPosition: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label={t("registerClient.contact.fields.email")}
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) =>
                      setFormData({ ...formData, contactEmail: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("registerClient.contact.fields.phone")}
                    value={formData.contactPhone}
                    onChange={(e) =>
                      setFormData({ ...formData, contactPhone: e.target.value })
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Plan Selection */}
        {activeStep === 2 && (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Settings sx={{ mr: 1, color: "#f59e0b", fontSize: 28 }} />
                  <Typography variant="h6" fontWeight={600}>
                    {t("registerClient.plan.title")}
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {plans.map((plan) => (
                    <Grid item xs={12} md={4} key={plan.id}>
                      <Card
                        sx={{
                          border:
                            formData.planId === plan.id
                              ? "2px solid #6366f1"
                              : "1px solid #e5e7eb",
                          cursor: "pointer",
                          transition: "all 0.3s",
                          "&:hover": { boxShadow: 3 },
                        }}
                        onClick={() =>
                          setFormData({ ...formData, planId: plan.id })
                        }
                      >
                        <CardContent>
                          <Typography variant="h6" fontWeight={600} gutterBottom>
                            {plan.name}
                          </Typography>
                          <Typography
                            variant="h4"
                            fontWeight={700}
                            color="primary"
                            gutterBottom
                          >
                            ${formatMoney(plan.amount)}
                            <Typography
                              component="span"
                              variant="body2"
                              color="textSecondary"
                            >
                              {t("registerClient.plan.perMonth")}
                            </Typography>
                          </Typography>
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              ✓ {t("registerClient.plan.includes.users")}: {plan.users || 0}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              ✓ {t("registerClient.plan.includes.connections")}: {plan.connections || 0}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              ✓ {t("registerClient.plan.includes.queues")}: {plan.queues || 0}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ mt: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>{t("registerClient.plan.billingCycle")}</InputLabel>
                    <Select
                      value={formData.billingCycle}
                      onChange={(e) =>
                        setFormData({ ...formData, billingCycle: e.target.value })
                      }
                      label={t("registerClient.plan.billingCycle")}
                    >
                      <MenuItem value="monthly">{t("registerClient.plan.billingMonthly")}</MenuItem>
                      <MenuItem value="annual">
                        {t("registerClient.plan.billingAnnual")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t("registerClient.addons.title")}
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {addonsList.map((addon) => (
                    <Grid item xs={12} sm={6} key={addon.key}>
                      <Card
                        sx={{
                          border: formData.addons.includes(addon.key)
                            ? "2px solid #10b981"
                            : "1px solid #e5e7eb",
                          cursor: "pointer",
                        }}
                        onClick={() => handleAddonToggle(addon.key)}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="body1" fontWeight={600}>
                              {addon.name}
                            </Typography>
                            <Chip
                              label={t("registerClient.addons.pricePerMonth", { amount: formatMoney(addon.amount) })}
                              size="small"
                              color={
                                formData.addons.includes(addon.key)
                                  ? "success"
                                  : "default"
                              }
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t("registerClient.notes.title")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder={t("registerClient.notes.placeholder")}
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                />
              </CardContent>
            </Card>

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2" fontWeight={600}>
                {t("registerClient.total.label")}:{" "}
                <Typography component="span" variant="h6" color="primary">
                  ${formatMoney(calculateTotal())}
                  {formData.billingCycle === "annual"
                    ? t("registerClient.total.perYear")
                    : t("registerClient.total.perMonth")}
                </Typography>
              </Typography>
            </Alert>
          </>
        )}
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          startIcon={<ArrowBack />}
        >
          {t("registerClient.buttons.back")}
        </Button>
        <Box>
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForward />}
              sx={{
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              }}
            >
              {t("registerClient.buttons.next")}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              startIcon={<CheckCircle />}
              sx={{
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
              }}
            >
              {t("registerClient.buttons.submit")}
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default RegisterClient;
